import type {
    ChecklistTemplate,
    AssignedChecklist,
    ChecklistStep,
    ChecklistSummary,
    CreateTemplatePayload,
    UpdateTemplatePayload,
    AssignChecklistPayload,
    BulkAssignPayload,
    RecurringSchedule,
    CreateRecurringSchedulePayload,
    CompletionReport,
    BottleneckReport,
    ReassignStepPayload,
    NudgeStepPayload,
    UserResult,
} from './types';
import { apiClient } from '@/lib/api';

const BASE = '/checklists';

// ── Templates ────────────────────────────────────────

export async function listTemplates(
    category?: string
): Promise<ChecklistTemplate[]> {
    const { data } = await apiClient.get(`${BASE}/templates`, {
        params: category ? { category } : undefined,
    });
    return data;
}

export async function getTemplate(
    id: string
): Promise<ChecklistTemplate> {
    const { data } = await apiClient.get(`${BASE}/templates/${id}`);
    return data;
}

export async function createTemplate(
    payload: CreateTemplatePayload
): Promise<ChecklistTemplate> {
    const { data } = await apiClient.post(`${BASE}/templates`, payload);
    return data;
}

export async function deactivateTemplate(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/templates/${id}`);
}

// ── Checklists ───────────────────────────────────────

export async function listChecklists(params?: {
    status?: string;
    template_id?: string;
    subject_user_id?: string;
    page?: number;
    per_page?: number;
}): Promise<{ data: AssignedChecklist[]; total: number }> {
    const { data } = await apiClient.get(BASE, { params });
    return data;
}

export async function getChecklist(
    id: string
): Promise<AssignedChecklist> {
    const { data } = await apiClient.get(`${BASE}/${id}`);
    return data;
}

export async function assignChecklist(
    payload: AssignChecklistPayload
): Promise<AssignedChecklist> {
    const { data } = await apiClient.post(BASE, payload);
    return data;
}

export async function bulkAssign(
    payload: BulkAssignPayload
): Promise<{ assigned: number; data: AssignedChecklist[] }> {
    const { data } = await apiClient.post(`${BASE}/bulk`, payload);
    return data;
}

export async function cancelChecklist(id: string): Promise<void> {
    await apiClient.put(`${BASE}/${id}/cancel`);
}

// ── Steps ────────────────────────────────────────────

export async function updateStep(
    checklistId: string,
    stepId: string,
    payload: { status?: string; evidence_url?: string; notes?: string }
): Promise<ChecklistStep> {
    const { data } = await apiClient.put(
        `${BASE}/${checklistId}/steps/${stepId}`,
        payload
    );
    return data;
}

export async function completeStep(
    checklistId: string,
    stepId: string
): Promise<void> {
    await apiClient.post(`${BASE}/${checklistId}/steps/${stepId}/complete`);
}

export async function blockStep(
    checklistId: string,
    stepId: string,
    reason: string
): Promise<ChecklistStep> {
    const { data } = await apiClient.post(
        `${BASE}/${checklistId}/steps/${stepId}/block`,
        { reason }
    );
    return data;
}

// ── Dashboard ────────────────────────────────────────

export async function getSummary(): Promise<ChecklistSummary> {
    const { data } = await apiClient.get(`${BASE}/summary`);
    return data;
}

// -- User Search --

export async function searchUsers(query: string): Promise<UserResult[]> {
    const { data } = await apiClient.get('/users', {
        params: { search: query, limit: 20 },
    });
    return data;
}

// -- Template Update --

export async function updateTemplate(
    id: string,
    payload: UpdateTemplatePayload
): Promise<ChecklistTemplate> {
    const { data } = await apiClient.put(
        BASE + '/templates/' + id,
        payload
    );
    return data;
}

// -- Step Actions --

export async function skipStep(
    checklistId: string,
    stepId: string,
    reason: string
): Promise<ChecklistStep> {
    const { data } = await apiClient.post(
        BASE + '/' + checklistId + '/steps/' + stepId + '/skip',
        { reason }
    );
    return data;
}

export async function reassignStep(
    checklistId: string,
    stepId: string,
    payload: ReassignStepPayload
): Promise<ChecklistStep> {
    const { data } = await apiClient.put(
        BASE + '/' + checklistId + '/steps/' + stepId + '/reassign',
        payload
    );
    return data;
}

export async function nudgeStep(
    checklistId: string,
    stepId: string,
    payload?: NudgeStepPayload
): Promise<void> {
    await apiClient.post(
        BASE + '/' + checklistId + '/steps/' + stepId + '/nudge',
        payload || {}
    );
}

// -- Recurring Schedules --

export async function listRecurringSchedules(): Promise<RecurringSchedule[]> {
    const { data } = await apiClient.get(BASE + '/recurring');
    return data;
}

export async function createRecurringSchedule(
    payload: CreateRecurringSchedulePayload
): Promise<RecurringSchedule> {
    const { data } = await apiClient.post(BASE + '/recurring', payload);
    return data;
}

export async function deactivateRecurringSchedule(
    id: string
): Promise<void> {
    await apiClient.delete(BASE + '/recurring/' + id);
}

// -- Reports --

export async function getCompletionReport(): Promise<CompletionReport[]> {
    const { data } = await apiClient.get(BASE + '/reports/summary');
    return data;
}

export async function getBottleneckReport(): Promise<BottleneckReport[]> {
    const { data } = await apiClient.get(BASE + '/reports/bottlenecks');
    return data;
}